
import React, { useState, useEffect } from 'react';
import { AppSettings, AIProvider, CostMode, GeminiModel, LiveVoiceProvider, SearchRegion, TTSProvider, UIThemeColors, UIThemePreset } from '../../types';
import { OPENAI_REALTIME_VOICES, VOICES } from '../../constants';
import { OpenRouterService, OpenRouterModel } from '../../services/openRouterService';
import { LocalLLMService } from '../../services/localLLMService';
import { AuthService, AuthUser, ManagedAuthUser } from '../../services/authService';
import { ServerSettingsService, ServerSettingsSummary, BosonCustomVoice } from '../../services/serverSettingsService';
import { VoiceLabService } from '../../services/voice/voiceLabService';
import { VoiceStackService } from '../../services/voice/voiceStackService';
import { applyUiTheme, DEFAULT_UI_THEME, DEFAULT_UI_THEME_PRESETS } from '../../services/settingsService';

interface SettingsModalProps {
  settings: AppSettings;
  onSave: (settings: AppSettings) => void;
  onClose: () => void;
}

const SETTINGS_TABS = [
  { id: 'api', label: 'APIs & Modellen' },
  { id: 'appearance', label: 'Uiterlijk' },
  { id: 'persona', label: 'Persona' },
  { id: 'regels', label: 'Regels' },
  { id: 'stem', label: 'Stem' },
  { id: 'account', label: 'Account' },
] as const;

const THEME_FIELDS: Array<{ key: keyof UIThemeColors; label: string; hint: string }> = [
  { key: 'canvas', label: 'Canvas', hint: 'Globale achtergrond van de app.' },
  { key: 'canvasAccent', label: 'Canvas Accent', hint: 'Zachte kleur in gradients en gloed.' },
  { key: 'surface', label: 'Panelen', hint: 'Standaard vlakken en kaarten.' },
  { key: 'surfaceAlt', label: 'Panelen Alt', hint: 'Tweede niveau van vlakken.' },
  { key: 'sidebar', label: 'Sidebar', hint: 'Rail en contextkolom links.' },
  { key: 'input', label: 'Velden', hint: 'Inputs, selects en tekstvakken.' },
  { key: 'border', label: 'Randen', hint: 'Lijnen, outlines en verdelingen.' },
  { key: 'text', label: 'Hoofdtekst', hint: 'Primaire tekstkleur.' },
  { key: 'mutedText', label: 'Secundaire Tekst', hint: 'Labels en ondersteunende copy.' },
  { key: 'accent', label: 'Accent', hint: 'Primaire actie- en focuskleur.' },
  { key: 'accentText', label: 'Accent Tekst', hint: 'Tekst op accentknoppen.' },
];

const defaultPresetIds = new Set(DEFAULT_UI_THEME_PRESETS.map((preset) => preset.id));

const slugifyPresetName = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const PROVIDER_OPTIONS: Array<{ id: AIProvider; label: string; hint: string }> = [
  { id: AIProvider.OPENROUTER, label: 'OpenRouter', hint: 'Beste keuze voor groeiende modelcatalogus en eenvoudige providerwissel.' },
  { id: AIProvider.GEMINI, label: 'Gemini', hint: 'Directe Google-modellen met vaste selectie.' },
  { id: AIProvider.OLLAMA, label: 'Ollama', hint: 'Lokale modellen op je eigen machine.' },
  { id: AIProvider.LMSTUDIO, label: 'LM Studio', hint: 'Lokale endpoint met eigen modelserver.' },
];

const COST_MODE_OPTIONS: Array<{ id: CostMode; label: string; hint: string }> = [
  { id: CostMode.FREE_FIRST, label: 'Free First', hint: 'Alleen gratis OpenRouter-modellen en gratis router.' },
  { id: CostMode.CHEAP_PAID_FALLBACK, label: 'Cheap Fallback', hint: 'Eerst gratis, daarna goedkoopste betaalde fallback.' },
  { id: CostMode.BEST_QUALITY, label: 'Best Quality', hint: 'Premium router alleen wanneer je dit bewust kiest.' },
];

const SEARCH_REGION_OPTIONS: Array<{ id: SearchRegion; label: string; hint: string }> = [
  { id: SearchRegion.NL, label: 'NL', hint: 'Nederlandse bedrijven en Nederlandse contactbronnen.' },
  { id: SearchRegion.BENELUX, label: 'Benelux', hint: 'Nederland, België en Luxemburg.' },
  { id: SearchRegion.EUROPE, label: 'Europa', hint: 'Europese bedrijven; contacteerbaarheid blijft leidend.' },
];

const voiceProvidersList = [
  {
    id: LiveVoiceProvider.ELEVENLABS_REALTIME,
    title: 'ElevenLabs Realtime',
    badge: 'AANBEVOLEN',
    badgeClass: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
    tech: 'WebRTC / Scribe v2',
    desc: 'Directe audioverbinding voor ultra-lage latency, emotie-herkenning en natuurlijke conversaties. Ideaal voor premium live bellen.',
    accentColor: 'from-emerald-500/20 to-teal-500/20 border-emerald-500/40 text-emerald-400 focus:ring-emerald-500',
    bgActive: 'bg-emerald-500/5 border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.1)]',
    icon: (
      <svg className="w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    )
  },
  {
    id: LiveVoiceProvider.OPENAI,
    title: 'OpenAI Realtime',
    badge: 'REALTIME',
    badgeClass: 'bg-violet-500/10 text-violet-300 border-violet-500/20',
    tech: 'WebRTC / GPT-4o',
    desc: "Directe audio stream met OpenAI's native spraakmodel. Zeer snelle reactietijd en vloeiende, menselijke stemmen.",
    accentColor: 'from-violet-500/20 to-purple-500/20 border-violet-500/40 text-violet-400 focus:ring-violet-500',
    bgActive: 'bg-violet-500/5 border-violet-500/30 shadow-[0_0_15px_rgba(139,92,246,0.1)]',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    )
  },
  {
    id: LiveVoiceProvider.ELEVENLABS,
    title: 'ElevenLabs Classic',
    badge: 'TURN-BASED',
    badgeClass: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    tech: 'OpenRouter + ElevenLabs TTS',
    desc: 'Traditionele turn-based methode. Denkt na via OpenRouter intelligentie en genereert daarna spraak via ElevenLabs TTS.',
    accentColor: 'from-amber-500/20 to-orange-500/20 border-amber-500/40 text-amber-400 focus:ring-amber-500',
    bgActive: 'bg-amber-500/5 border-amber-500/30 shadow-[0_0_15px_rgba(245,158,11,0.1)]',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    )
  },
  {
    id: LiveVoiceProvider.GEMINI,
    title: 'Gemini Voice',
    badge: 'KOSTENEFFICIËNT',
    badgeClass: 'bg-sky-500/10 text-sky-300 border-sky-500/20',
    tech: 'Gemini 2.5 + Gemini TTS',
    desc: 'Kostenefficiënte fallback engine. Maakt direct gebruik van Google Gemini voor zowel de intelligentie als de spraaksynthese.',
    accentColor: 'from-sky-500/20 to-blue-500/20 border-sky-500/40 text-sky-400 focus:ring-sky-500',
    bgActive: 'bg-sky-500/5 border-sky-500/30 shadow-[0_0_15px_rgba(14,165,233,0.1)]',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    )
  }
];

const SUPERTONIC_VOICE_OPTIONS = ['F1', 'F2', 'F3', 'F4', 'F5', 'M1', 'M2', 'M3', 'M4', 'M5'] as const;

const isGeminiTextModel = (modelId: string) => [
  GeminiModel.FLASH_3,
  GeminiModel.PRO_3,
  GeminiModel.LITE_3,
  GeminiModel.THINKING_3,
  GeminiModel.FLASH_4,
  GeminiModel.PRO_4,
  GeminiModel.LITE_4,
  GeminiModel.ARGON,
].includes(modelId as GeminiModel);

const formatContextLength = (tokens: number) => {
  if (!tokens) return '';
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M ctx`;
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}k ctx`;
  return `${tokens} ctx`;
};

const formatOpenRouterModelLabel = (model: OpenRouterModel, suffix?: string) => {
  const contextLabel = formatContextLength(model.context_length);
  const promptPrice = parseFloat(model.pricing?.prompt || '0');
  const completionPrice = parseFloat(model.pricing?.completion || '0');
  const highestPrice = Math.max(promptPrice, completionPrice);
  const priceLabel = Number.isFinite(highestPrice) && highestPrice > 0 ? `$${highestPrice.toFixed(2)}/M max` : 'Gratis';
  const parts = [model.name];

  if (suffix) parts.push(suffix);
  if (contextLabel) parts.push(contextLabel);
  if (priceLabel) parts.push(priceLabel);

  return parts.join(' • ');
};

const SettingsModal: React.FC<SettingsModalProps> = ({ settings, onSave, onClose }) => {
  const [localSettings, setLocalSettings] = useState<AppSettings>(settings);
  const [activeTab, setActiveTab] = useState<(typeof SETTINGS_TABS)[number]['id']>('api');
  const [orModels, setOrModels] = useState<{ free: OpenRouterModel[], paid: OpenRouterModel[] }>({ free: [], paid: [] });
  const [localModels, setLocalModels] = useState<string[]>([]);
  const [localModelsError, setLocalModelsError] = useState('');
  const [latencies, setLatencies] = useState<Record<string, number>>({});
  const [modelErrors, setModelErrors] = useState<Record<string, string>>({});
  const [checkingPing, setCheckingPing] = useState<Record<string, boolean>>({});
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(() => AuthService.getStoredUser());
  const [accountName, setAccountName] = useState(AuthService.getStoredUser()?.name || '');
  const [accountEmail, setAccountEmail] = useState(AuthService.getStoredUser()?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [accountStatus, setAccountStatus] = useState('');
  const [accountError, setAccountError] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [managedUsers, setManagedUsers] = useState<ManagedAuthUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [usersStatus, setUsersStatus] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [resetPasswords, setResetPasswords] = useState<Record<string, string>>({});
  const [resettingUserId, setResettingUserId] = useState('');
  const [deletingUserId, setDeletingUserId] = useState('');
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioInputsLoading, setAudioInputsLoading] = useState(false);
  const [audioInputsError, setAudioInputsError] = useState('');
  const [serverSettings, setServerSettings] = useState<ServerSettingsSummary | null>(null);
  const [googlePlacesKey, setGooglePlacesKey] = useState('');
  const [googlePlacesStatus, setGooglePlacesStatus] = useState('');
  const [googlePlacesError, setGooglePlacesError] = useState('');
  const [googlePlacesSaving, setGooglePlacesSaving] = useState(false);
  const [googlePlacesTesting, setGooglePlacesTesting] = useState(false);
  const [voiceServerSettings, setVoiceServerSettings] = useState<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'boson'> | null>(null);
  const [elevenLabsKey, setElevenLabsKey] = useState('');
  const [elevenLabsVoiceId, setElevenLabsVoiceId] = useState('');
  const [bosonApiKey, setBosonApiKey] = useState('');
  const [bosonSelectedVoice, setBosonSelectedVoice] = useState<'chloe' | 'eleanor' | 'jake' | 'marcus' | 'nora' | 'oliver' | 'berlinda'>('nora');
  const [bosonCustomVoices, setBosonCustomVoices] = useState<BosonCustomVoice[]>([]);
  const [bosonCloneFile, setBosonCloneFile] = useState<File | null>(null);
  const [bosonCloneText, setBosonCloneText] = useState('');
  const [bosonCloneTitle, setBosonCloneTitle] = useState('Mijn BDAT stem');
  const [deepgramKey, setDeepgramKey] = useState('');
  const [voiceAgentOpen, setVoiceAgentOpen] = useState(false);
  const [voiceRuntimeMode, setVoiceRuntimeMode] = useState<'auto' | 'mac-local' | 'pc-nvidia' | 'vps-gpu'>('auto');
  const [preferredLocalTts, setPreferredLocalTts] = useState<'supertonic' | 'voxcpm' | 'boson-higgs' | 'elevenlabs'>('elevenlabs');
  const [supertonicBaseUrl, setSupertonicBaseUrl] = useState('');
  const [supertonicVoiceStyle, setSupertonicVoiceStyle] = useState<(typeof SUPERTONIC_VOICE_OPTIONS)[number]>('M1');
  const [supertonicCharacter, setSupertonicCharacter] = useState<'business' | 'free'>('business');
  const [voxcpmBaseUrl, setVoxcpmBaseUrl] = useState('');
  const [voxcpmAuthToken, setVoxcpmAuthToken] = useState('');
  const [voiceEngineStatus, setVoiceEngineStatus] = useState<Awaited<ReturnType<typeof ServerSettingsService.getVoiceEngineStatus>> | null>(null);
  const [voiceLabStatus, setVoiceLabStatus] = useState('');
  const [voiceLabError, setVoiceLabError] = useState('');
  const [voiceLabBusy, setVoiceLabBusy] = useState('');

  const moods: AppSettings['voiceMood'][] = ['Neutral', 'Cheerful', 'Professional', 'CasualProfessional', 'Calm', 'Excited', 'Serious'];
  const speeds: AppSettings['voiceSpeed'][] = ['Slow', 'Normal', 'Fast'];

  const geminiModels = [
    GeminiModel.FLASH_4,
    GeminiModel.PRO_4,
    GeminiModel.LITE_4,
    GeminiModel.ARGON,
    GeminiModel.FLASH_3,
    GeminiModel.PRO_3,
    GeminiModel.LITE_3,
    GeminiModel.THINKING_3,
  ];

  // OpenRouter Models effect
  useEffect(() => {
    if (localSettings.provider === AIProvider.OPENROUTER && localSettings.openRouterKey) {
      OpenRouterService.fetchModels(localSettings.openRouterKey).then(setOrModels);
    } else if ((localSettings.provider === AIProvider.OLLAMA || localSettings.provider === AIProvider.LMSTUDIO) && localSettings.localUrl) {
      LocalLLMService.fetchLocalModelsDetailed(localSettings.localUrl, localSettings.localApiKey).then(({ models, error }) => {
        setLocalModels(models);
        setLocalModelsError(error || '');
      });
    } else {
      setLocalModels([]);
      setLocalModelsError('');
    }
  }, [localSettings.provider, localSettings.openRouterKey, localSettings.localUrl, localSettings.localApiKey]);

  useEffect(() => {
    const storedUser = AuthService.getStoredUser();
    setCurrentUser(storedUser);
    setAccountName(storedUser?.name || '');
    setAccountEmail(storedUser?.email || '');
  }, []);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const loadAudioInputs = async (requestAccess = false) => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      setAudioInputs([]);
      setAudioInputsError('Deze browser ondersteunt geen microfoonselectie.');
      return;
    }

    setAudioInputsLoading(true);
    setAudioInputsError('');

    try {
      if (requestAccess) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter((device) => device.kind === 'audioinput');
      setAudioInputs(inputs);

      if (!inputs.length) {
        setAudioInputsError('Geen microfoons gevonden.');
      }
    } catch (error: any) {
      setAudioInputs([]);
      setAudioInputsError(error?.message || 'Microfoons laden mislukt.');
    } finally {
      setAudioInputsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'account' || currentUser?.role !== 'ADMIN') return;

    let cancelled = false;

    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError('');

      try {
        const users = await AuthService.listUsers();
        if (cancelled) return;
        setManagedUsers(users);
      } catch (error: any) {
        if (cancelled) return;
        setUsersError(error?.message || 'Gebruikers laden mislukt.');
      } finally {
        if (!cancelled) {
          setUsersLoading(false);
        }
      }
    };

    void loadUsers();

    return () => {
      cancelled = true;
    };
  }, [activeTab, currentUser?.role]);

  useEffect(() => {
    if (activeTab !== 'stem') return;
    void loadAudioInputs(false);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'stem') return;

    let cancelled = false;
    const loadVoiceSettings = async () => {
      setVoiceLabError('');
      try {
        const settings = await ServerSettingsService.getVoiceSettings();
        if (cancelled) return;
        setVoiceServerSettings(settings);
        setElevenLabsVoiceId(settings.elevenLabs.voiceId || '');
        setVoiceRuntimeMode(settings.engines.voiceRuntimeMode);
        setPreferredLocalTts(settings.engines.preferredLocalTts);
        setSupertonicBaseUrl(settings.engines.supertonicBaseUrl || '');
        setSupertonicVoiceStyle((settings.engines.supertonicVoiceStyle || 'M1') as (typeof SUPERTONIC_VOICE_OPTIONS)[number]);
        setSupertonicCharacter(settings.engines.supertonicCharacter || 'business');
        setVoxcpmBaseUrl(settings.engines.voxcpmBaseUrl || '');
        setBosonCustomVoices(settings.bosonCustomVoices || []);
      } catch (error: any) {
        if (!cancelled) setVoiceLabError(error?.message || 'Voice server-config laden mislukt.');
      }
    };

    void loadVoiceSettings();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    applyUiTheme(localSettings.uiTheme);
  }, [localSettings.uiTheme]);

  useEffect(() => {
    if (activeTab !== 'api') return;

    let cancelled = false;

    const loadServerSettings = async () => {
      setGooglePlacesError('');

      try {
        const nextSettings = await ServerSettingsService.getSettings();
        if (cancelled) return;
        setServerSettings(nextSettings);
      } catch (error: any) {
        if (cancelled) return;
        setGooglePlacesError(error?.message || 'Server-config laden mislukt.');
      }
    };

    void loadServerSettings();

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    return () => {
      applyUiTheme(settings.uiTheme);
    };
  }, [settings.uiTheme]);

  const updateThemeColor = (key: keyof UIThemeColors, value: string) => {
    setLocalSettings((current) => ({
      ...current,
      uiTheme: {
        ...current.uiTheme,
        [key]: value,
      },
    }));
  };

  const applyThemePreset = (preset: UIThemePreset) => {
    setLocalSettings((current) => ({
      ...current,
      uiTheme: { ...preset.colors },
    }));
  };

  const saveCurrentThemeAsPreset = () => {
    const name = window.prompt('Naam voor deze kleurpreset');
    const trimmedName = name?.trim();
    if (!trimmedName) return;

    const baseId = slugifyPresetName(trimmedName) || crypto.randomUUID();
    const uniqueId = defaultPresetIds.has(baseId) ? `custom-${baseId}-${Date.now()}` : `custom-${baseId}`;

    setLocalSettings((current) => ({
      ...current,
      uiThemePresets: [
        ...current.uiThemePresets.filter((preset) => preset.id !== uniqueId),
        {
          id: uniqueId,
          name: trimmedName,
          colors: { ...current.uiTheme },
        },
      ],
    }));
  };

  const deleteThemePreset = (presetId: string) => {
    if (defaultPresetIds.has(presetId)) return;

    setLocalSettings((current) => ({
      ...current,
      uiThemePresets: current.uiThemePresets.filter((preset) => preset.id !== presetId),
    }));
  };

  const isPresetActive = (preset: UIThemePreset) =>
    THEME_FIELDS.every(
      ({ key }) =>
        preset.colors[key].toLowerCase() === (localSettings.uiTheme[key] || '').toLowerCase(),
    );

  const handleClose = () => {
    applyUiTheme(settings.uiTheme);
    onClose();
  };

  const handleSaveSettings = () => {
    onSave(localSettings);
    onClose();
  };

  const handleProviderChange = (provider: AIProvider) => {
    setLocalModelsError('');
    const browserHostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const defaultLmStudioUrl = browserHostname === 'host.docker.internal'
      ? 'http://host.docker.internal:1234'
      : LocalLLMService.DEFAULT_LMSTUDIO_URL;

    if (provider === AIProvider.LMSTUDIO) {
      const shouldUseDefaultLmStudioUrl =
        !localSettings.localUrl ||
        localSettings.localUrl === LocalLLMService.DEFAULT_OLLAMA_URL ||
        localSettings.localUrl === 'http://127.0.0.1:11434';

      setLocalSettings({
        ...localSettings,
        provider,
        localUrl: shouldUseDefaultLmStudioUrl ? defaultLmStudioUrl : localSettings.localUrl,
        localApiKey: localSettings.localApiKey || 'lm-studio',
      });
      return;
    }

    if (provider === AIProvider.OLLAMA) {
      const shouldUseDefaultOllamaUrl =
        !localSettings.localUrl ||
        localSettings.localUrl === LocalLLMService.DEFAULT_LMSTUDIO_URL ||
        localSettings.localUrl === 'http://127.0.0.1:1234';

      setLocalSettings({
        ...localSettings,
        provider,
        localUrl: shouldUseDefaultOllamaUrl ? LocalLLMService.DEFAULT_OLLAMA_URL : localSettings.localUrl,
      });
      return;
    }

    if (provider === AIProvider.OPENROUTER) {
      setLocalSettings({
        ...localSettings,
        provider,
        costMode: localSettings.costMode || CostMode.FREE_FIRST,
        defaultModel: (!localSettings.defaultModel || isGeminiTextModel(localSettings.defaultModel))
          ? OpenRouterService.AUTO_MODEL_ID
          : localSettings.defaultModel,
        researchModel: (!localSettings.researchModel || isGeminiTextModel(localSettings.researchModel))
          ? OpenRouterService.AUTO_MODEL_ID
          : localSettings.researchModel,
      });
      return;
    }

    setLocalSettings({ ...localSettings, provider });
  };

  const applyCostMode = (costMode: CostMode) => {
    const modelForMode = costMode === CostMode.BEST_QUALITY
      ? OpenRouterService.PAID_AUTO_MODEL_ID
      : OpenRouterService.AUTO_MODEL_ID;

    setLocalSettings((current) => ({
      ...current,
      costMode,
      defaultModel: modelForMode,
      researchModel: modelForMode,
    }));
  };

  const handleSaveGooglePlacesKey = async () => {
    setGooglePlacesSaving(true);
    setGooglePlacesStatus('');
    setGooglePlacesError('');

    try {
      const nextSettings = await ServerSettingsService.saveGooglePlacesKey(googlePlacesKey);
      setServerSettings(nextSettings);
      setGooglePlacesKey('');
      setGooglePlacesStatus('Google Places key server-side opgeslagen.');
    } catch (error: any) {
      setGooglePlacesError(error?.message || 'Google Places key opslaan mislukt.');
    } finally {
      setGooglePlacesSaving(false);
    }
  };

  const handleClearGooglePlacesKey = async () => {
    setGooglePlacesSaving(true);
    setGooglePlacesStatus('');
    setGooglePlacesError('');

    try {
      const nextSettings = await ServerSettingsService.clearGooglePlacesKey();
      setServerSettings(nextSettings);
      setGooglePlacesKey('');
      setGooglePlacesStatus(nextSettings.googlePlaces.configured
        ? 'Server-key verwijderd; env-key is nog actief.'
        : 'Google Places key verwijderd.');
    } catch (error: any) {
      setGooglePlacesError(error?.message || 'Google Places key wissen mislukt.');
    } finally {
      setGooglePlacesSaving(false);
    }
  };

  const handleTestGooglePlacesKey = async () => {
    setGooglePlacesTesting(true);
    setGooglePlacesStatus('');
    setGooglePlacesError('');

    try {
      const result = await ServerSettingsService.testGooglePlacesKey(googlePlacesKey);
      setGooglePlacesStatus(result.candidate?.name
        ? `Google Places werkt: ${result.candidate.name}.`
        : `Google Places werkt (${result.status || 'OK'}).`);
    } catch (error: any) {
      setGooglePlacesError(error?.message || 'Google Places test mislukt.');
    } finally {
      setGooglePlacesTesting(false);
    }
  };

  const playVoiceLabAudio = async (audio: ArrayBuffer) => {
    const url = URL.createObjectURL(new Blob([audio], { type: 'audio/mpeg' }));
    const element = new Audio(url);
    await element.play();
    element.onended = () => URL.revokeObjectURL(url);
  };

  const refreshVoiceEngineStatus = async () => {
    setVoiceLabBusy('engine-status');
    setVoiceLabError('');
    try {
      const status = await ServerSettingsService.getVoiceEngineStatus();
      setVoiceEngineStatus(status);
      setVoiceLabStatus(`Voice engines bijgewerkt. Actief: ${status.runtime.selectedProvider}.`);
    } catch (error: any) {
      setVoiceLabError(error?.message || 'Voice engine status laden mislukt.');
    } finally {
      setVoiceLabBusy('');
    }
  };

  const saveVoiceEngineSettings = async () => {
    setVoiceLabBusy('engine-save');
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const settings = await ServerSettingsService.saveVoiceEngines({
        voiceRuntimeMode,
        preferredLocalTts,
        supertonicBaseUrl,
        supertonicVoiceStyle,
        supertonicCharacter,
        voxcpmBaseUrl,
        voxcpmAuthToken,
      });
      setVoiceServerSettings(settings);
      setVoxcpmAuthToken('');
      setVoiceLabStatus('VoiceAgent engine-config server-side opgeslagen.');
      await refreshVoiceEngineStatus();
    } catch (error: any) {
      setVoiceLabError(error?.message || 'VoiceAgent engine-config opslaan mislukt.');
      setVoiceLabBusy('');
    }
  };

  const saveBosonApiKey = async () => {
    if (!bosonApiKey.trim()) {
      setVoiceLabError('Boson API key is leeg.');
      return;
    }
    setVoiceLabBusy('boson-save');
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const settings = await ServerSettingsService.saveBoson({ apiKey: bosonApiKey.trim() });
      setVoiceServerSettings(settings);
      setBosonApiKey('');
      setVoiceLabStatus('Boson Higgs API key opgeslagen.');
      await refreshVoiceEngineStatus();
    } catch (error: any) {
      setVoiceLabError(error?.message || 'Boson key opslaan mislukt.');
    } finally {
      setVoiceLabBusy('');
    }
  };

  const clearBosonApiKey = async () => {
    setVoiceLabBusy('boson-clear');
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const settings = await ServerSettingsService.clearBoson();
      setVoiceServerSettings(settings);
      setBosonApiKey('');
      setVoiceLabStatus('Boson Higgs key verwijderd (terugval naar andere providers).');
      await refreshVoiceEngineStatus();
    } catch (error: any) {
      setVoiceLabError(error?.message || 'Boson key verwijderen mislukt.');
    } finally {
      setVoiceLabBusy('');
    }
  };

  const testBosonVoice = async () => {
    setVoiceLabBusy('boson-test');
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const result = await VoiceLabService.testBoson({ voice: bosonSelectedVoice });
      setVoiceLabStatus(result.message);
      await playVoiceLabAudio(result.audio);
    } catch (error: any) {
      const retryAfterSec = error?.retryAfterMs ? Math.ceil(error.retryAfterMs / 1000) : null;
      const msg = retryAfterSec
        ? `Boson rate-limit — probeer over ${retryAfterSec}s opnieuw.`
        : (error?.message || 'Boson Higgs stemtest mislukt.');
      setVoiceLabError(msg);
    } finally {
      setVoiceLabBusy('');
    }
  };

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error || new Error('File lezen mislukt.'));
      reader.readAsDataURL(file);
    });

  const cloneBosonVoice = async () => {
    if (!bosonCloneFile) {
      setVoiceLabError('Kies eerst een audiobestand (wav/mp3/flac/opus/pcm, 5-30s).');
      return;
    }
    if (!bosonCloneText.trim()) {
      setVoiceLabError('Transcript is verplicht — typ letterlijk wat er in de clip gezegd wordt.');
      return;
    }
    setVoiceLabBusy('boson-clone');
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const dataUrl = await readFileAsDataUrl(bosonCloneFile);
      const result = await ServerSettingsService.createBosonVoice({
        refAudio: dataUrl,
        refText: bosonCloneText.trim(),
        title: bosonCloneTitle.trim() || 'BDAT custom voice',
      });
      // Refresh lijst vanuit server (authoritatief).
      const fresh = await ServerSettingsService.listBosonVoices();
      setBosonCustomVoices(fresh.voices);
      setBosonCloneFile(null);
      setBosonCloneText('');
      setVoiceLabStatus(`Stem gekloond: ${result.voiceId.slice(0, 12)}… (${result.title})`);
    } catch (error: any) {
      const retryAfterSec = error?.retryAfterMs ? Math.ceil(error.retryAfterMs / 1000) : null;
      const msg = retryAfterSec
        ? `Boson rate-limit — wacht ${retryAfterSec}s en probeer opnieuw.`
        : (error?.message || 'Custom voice kloon mislukt.');
      setVoiceLabError(msg);
    } finally {
      setVoiceLabBusy('');
    }
  };

  const deleteBosonVoice = async (voiceId: string) => {
    if (!window.confirm('Deze custom voice verwijderen?')) return;
    setVoiceLabBusy(`boson-delete-${voiceId}`);
    setVoiceLabError('');
    try {
      const result = await ServerSettingsService.deleteBosonVoice(voiceId);
      setBosonCustomVoices(result.voices);
      setVoiceLabStatus('Custom voice verwijderd.');
    } catch (error: any) {
      setVoiceLabError(error?.message || 'Voice verwijderen mislukt.');
    } finally {
      setVoiceLabBusy('');
    }
  };

  const testBosonCustomVoice = async (voiceId: string) => {
    setVoiceLabBusy(`boson-custom-${voiceId}`);
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const result = await VoiceStackService.testCustomBosonVoice({ voiceId });
      setVoiceLabStatus(`Custom voice OK (${result.latencyMs}ms)`);
      await playVoiceLabAudio(result.audio);
    } catch (error: any) {
      const retryAfterSec = error?.retryAfterMs ? Math.ceil(error.retryAfterMs / 1000) : null;
      const msg = retryAfterSec
        ? `Boson rate-limit — probeer over ${retryAfterSec}s opnieuw.`
        : (error?.message || 'Custom voice test mislukt.');
      setVoiceLabError(msg);
    } finally {
      setVoiceLabBusy('');
    }
  };

  const testVoxcpmVoice = async () => {
    setVoiceLabBusy('voxcpm-test');
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const result = await VoiceLabService.testVoxcpm();
      setVoiceLabStatus(`VoxCPM2 OK (${result.latencyMs || 0}ms).`);
      await playVoiceLabAudio(result.audio);
    } catch (error: any) {
      setVoiceLabError(error?.message || 'VoxCPM2 test mislukt.');
    } finally {
      setVoiceLabBusy('');
    }
  };

  const runSupertonicAction = async (action: 'check' | 'setup' | 'start' | 'stop' | 'restart' | 'status') => {
    setVoiceLabBusy(`supertonic-${action}`);
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const result = await ServerSettingsService.runSupertonicAction(action);
      if (result.settings) {
        setVoiceServerSettings(result.settings);
        setVoiceRuntimeMode(result.settings.engines.voiceRuntimeMode);
        setPreferredLocalTts(result.settings.engines.preferredLocalTts);
        setSupertonicBaseUrl(result.settings.engines.supertonicBaseUrl || '');
        setSupertonicVoiceStyle((result.settings.engines.supertonicVoiceStyle || 'M1') as (typeof SUPERTONIC_VOICE_OPTIONS)[number]);
        setSupertonicCharacter(result.settings.engines.supertonicCharacter || 'business');
      }
      setVoiceLabStatus(`Supertonic ${action}: ${result.stdout || 'OK'}`);
      await refreshVoiceEngineStatus();
    } catch (error: any) {
      setVoiceLabError(error?.message || `Supertonic ${action} mislukt.`);
      setVoiceLabBusy('');
    }
  };

  const testSupertonicVoice = async () => {
    setVoiceLabBusy('supertonic-voice-test');
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const result = await VoiceLabService.testSupertonic({
        voiceStyle: supertonicVoiceStyle,
        character: supertonicCharacter,
      });
      setVoiceLabStatus(result.message);
      await playVoiceLabAudio(result.audio);
    } catch (error: any) {
      setVoiceLabError(error?.message || 'Supertonic stemtest mislukt.');
    } finally {
      setVoiceLabBusy('');
    }
  };

  const runVoiceLab = async (kind: 'mic' | 'brain' | 'voice' | 'full') => {
    setVoiceLabBusy(kind);
    setVoiceLabStatus('');
    setVoiceLabError('');

    try {
      if (kind === 'mic') {
        await loadAudioInputs(true);
        setVoiceLabStatus('Mic OK: invoerapparaten geladen.');
        return;
      }

      if (kind === 'brain') {
        setVoiceLabStatus(await VoiceLabService.testBrain(localSettings));
        return;
      }

      if (kind === 'voice') {
        const result = await VoiceLabService.testVoice();
        setVoiceLabStatus(result.message);
        await playVoiceLabAudio(result.audio);
        return;
      }

      const result = await VoiceLabService.testFullLoop(localSettings);
      setVoiceLabStatus(result.message);
      await playVoiceLabAudio(result.audio);
    } catch (error: any) {
      setVoiceLabError(error?.message || 'Voice Lab test mislukt.');
    } finally {
      setVoiceLabBusy('');
    }
  };

  const saveElevenLabsSettings = async () => {
    setVoiceLabBusy('elevenlabs-save');
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const settings = await ServerSettingsService.saveElevenLabs({
        apiKey: elevenLabsKey,
        voiceId: elevenLabsVoiceId,
      });
      setVoiceServerSettings(settings);
      setElevenLabsKey('');
      setVoiceLabStatus('ElevenLabs server-side opgeslagen.');
    } catch (error: any) {
      setVoiceLabError(error?.message || 'ElevenLabs opslaan mislukt.');
    } finally {
      setVoiceLabBusy('');
    }
  };

  const saveDeepgramSettings = async () => {
    setVoiceLabBusy('deepgram-save');
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const settings = await ServerSettingsService.saveDeepgram(deepgramKey);
      setVoiceServerSettings(settings);
      setDeepgramKey('');
      setVoiceLabStatus('Deepgram server-side opgeslagen.');
    } catch (error: any) {
      setVoiceLabError(error?.message || 'Deepgram opslaan mislukt.');
    } finally {
      setVoiceLabBusy('');
    }
  };

  const clearDeepgramSettings = async () => {
    setVoiceLabBusy('deepgram-clear');
    setVoiceLabStatus('');
    setVoiceLabError('');
    try {
      const settings = await ServerSettingsService.clearDeepgram();
      setVoiceServerSettings(settings);
      setDeepgramKey('');
      setVoiceLabStatus('Deepgram verwijderd. Calls gebruiken nu browser-spraakherkenning als STT fallback.');
    } catch (error: any) {
      setVoiceLabError(error?.message || 'Deepgram verwijderen mislukt.');
    } finally {
      setVoiceLabBusy('');
    }
  };

  const checkModelPing = async (type: 'chat' | 'research', modelId: string) => {
    const key = `${type}-${modelId}`;
    setCheckingPing(prev => ({ ...prev, [key]: true }));
    
    let ping = -1;
    let errorMessage = '';
    if (localSettings.provider === AIProvider.OPENROUTER && localSettings.openRouterKey) {
      const result = await OpenRouterService.checkHealthDetailed(localSettings.openRouterKey, modelId);
      ping = result.latency;
      errorMessage = result.error
        ? `${result.status ? `${result.status}: ` : ''}${result.error}`
        : '';
    } else if (localSettings.provider === AIProvider.OPENROUTER) {
      errorMessage = 'OpenRouter API key ontbreekt.';
    } else if ((localSettings.provider === AIProvider.OLLAMA || localSettings.provider === AIProvider.LMSTUDIO) && localSettings.localUrl) {
      ping = await LocalLLMService.checkHealth(localSettings.localUrl, modelId, localSettings.localApiKey);
    } else if (localSettings.provider === AIProvider.GEMINI) {
      // Dummy check for Gemini (could be real if service supported it)
      ping = Math.round(Math.random() * 200 + 100);
    }

    setLatencies(prev => ({ ...prev, [key]: ping }));
    setModelErrors(prev => ({ ...prev, [key]: errorMessage }));
    setCheckingPing(prev => ({ ...prev, [key]: false }));
  };

  const ModelStatusIndicator = ({ type, modelId }: { type: 'chat' | 'research', modelId: string }) => {
    const key = `${type}-${modelId}`;
    const ping = latencies[key];
    const error = modelErrors[key];
    const isChecking = checkingPing[key];

    return (
      <div className="flex items-center gap-2 mt-1">
        <div className={`w-1.5 h-1.5 rounded-full ${ping > 0 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]' : ping === -1 ? 'bg-red-500' : 'bg-slate-700'} ${isChecking ? 'animate-pulse' : ''}`} />
        <span className={`max-w-[260px] truncate text-[8px] font-mono ${error ? 'text-red-300' : 'text-slate-500'}`} title={error || undefined}>
          {isChecking ? 'Checking...' : ping > 0 ? `Ping: ${ping}ms` : error || (ping === -1 ? 'Offline' : 'Click to check')}
        </span>
        {!isChecking && (
          <button 
            onClick={() => checkModelPing(type, modelId)}
            className="text-[8px] text-indigo-400 hover:text-indigo-300 underline"
          >
            Test
          </button>
        )}
      </div>
    );
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setAccountStatus('');
    setAccountError('');

    try {
      const updatedUser = await AuthService.updateProfile(accountName.trim(), accountEmail.trim());
      setCurrentUser(updatedUser);
      setAccountName(updatedUser.name);
      setAccountEmail(updatedUser.email);
      setAccountStatus('Profiel bijgewerkt.');
    } catch (error: any) {
      setAccountError(error?.message || 'Profiel bijwerken mislukt.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmNewPassword) {
      setAccountError('Nieuwe wachtwoorden komen niet overeen.');
      setAccountStatus('');
      return;
    }

    setPasswordSaving(true);
    setAccountStatus('');
    setAccountError('');

    try {
      await AuthService.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setAccountStatus('Wachtwoord gewijzigd.');
    } catch (error: any) {
      setAccountError(error?.message || 'Wachtwoord wijzigen mislukt.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const reloadManagedUsers = async () => {
    const users = await AuthService.listUsers();
    setManagedUsers(users);
  };

  const handleCreateUser = async () => {
    setCreatingUser(true);
    setUsersStatus('');
    setUsersError('');

    try {
      await AuthService.createUser(newUserName.trim(), newUserEmail.trim(), newUserPassword);
      await reloadManagedUsers();
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setUsersStatus('Extra admin-account aangemaakt.');
    } catch (error: any) {
      setUsersError(error?.message || 'Gebruiker aanmaken mislukt.');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleResetManagedUserPassword = async (userId: string) => {
    const nextPassword = resetPasswords[userId]?.trim();
    if (!nextPassword) {
      setUsersError('Vul eerst een nieuw wachtwoord in voor deze gebruiker.');
      setUsersStatus('');
      return;
    }

    setResettingUserId(userId);
    setUsersStatus('');
    setUsersError('');

    try {
      await AuthService.resetUserPassword(userId, nextPassword);
      setResetPasswords((current) => ({ ...current, [userId]: '' }));
      setUsersStatus('Wachtwoord van gebruiker gereset.');
    } catch (error: any) {
      setUsersError(error?.message || 'Wachtwoord reset mislukt.');
    } finally {
      setResettingUserId('');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setDeletingUserId(userId);
    setUsersStatus('');
    setUsersError('');

    try {
      await AuthService.deleteUser(userId);
      await reloadManagedUsers();
      setUsersStatus('Gebruiker verwijderd.');
    } catch (error: any) {
      setUsersError(error?.message || 'Gebruiker verwijderen mislukt.');
    } finally {
      setDeletingUserId('');
    }
  };

  return (
    <div className="bdat-settings-modal fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-2 sm:p-4 backdrop-blur-md">
      <div className="bdat-soft-panel-strong flex max-h-[92vh] w-full max-w-[min(72rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-[2rem] text-[color:var(--bdat-text)] sm:rounded-[2.5rem]">
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .bdat-settings-modal .bdat-settings-card {
                background: color-mix(in srgb, var(--bdat-surface-alt) 82%, white);
                border: 1px solid var(--bdat-border);
                box-shadow: inset 0 1px 0 rgba(255,255,255,0.45);
              }
              .bdat-settings-modal input[type="text"],
              .bdat-settings-modal input[type="email"],
              .bdat-settings-modal input[type="password"],
              .bdat-settings-modal select,
              .bdat-settings-modal textarea {
                background: var(--bdat-input) !important;
                border: 1px solid var(--bdat-border) !important;
                color: var(--bdat-text) !important;
              }
              .bdat-settings-modal input::placeholder,
              .bdat-settings-modal textarea::placeholder {
                color: color-mix(in srgb, var(--bdat-muted) 82%, white) !important;
              }
              .bdat-settings-modal .bdat-settings-muted {
                color: var(--bdat-muted) !important;
              }
              .bdat-settings-modal [class*="bg-emerald-500/"] [class*="text-emerald-"],
              .bdat-settings-modal [class*="bg-emerald-400/"] [class*="text-emerald-"] {
                color: #065f46 !important;
              }
              .bdat-settings-modal [class*="bg-indigo-500/"] [class*="text-indigo-"],
              .bdat-settings-modal [class*="bg-indigo-400/"] [class*="text-indigo-"],
              .bdat-settings-modal [class*="bg-fuchsia-500/"] [class*="text-fuchsia-"] {
                color: #3730a3 !important;
              }
              .bdat-settings-modal [class*="bg-cyan-500/"] [class*="text-cyan-"] {
                color: #155e75 !important;
              }
              .bdat-settings-modal [class*="bg-amber-500/"] [class*="text-amber-"] {
                color: #92400e !important;
              }
              .bdat-settings-modal [class*="bg-emerald-500/"] [class*="text-slate-"],
              .bdat-settings-modal [class*="bg-indigo-500/"] [class*="text-slate-"],
              .bdat-settings-modal [class*="bg-cyan-500/"] [class*="text-slate-"],
              .bdat-settings-modal [class*="bg-fuchsia-500/"] [class*="text-slate-"] {
                color: #334155 !important;
              }
            `,
          }}
        />
        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-[color:var(--bdat-border)] px-5 py-5 sm:px-6">
          <div>
            <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Instellingen</h2>
            <p className="mt-1 text-xs bdat-settings-muted">Werkruimtebreedte, spraak en kleuren beheer je hier centraal.</p>
          </div>
          <button onClick={handleClose} className="rounded-2xl border border-[color:var(--bdat-border)] p-2.5 text-[color:var(--bdat-muted)] transition-colors hover:text-[color:var(--bdat-text)]">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-[color:var(--bdat-border)] px-3 py-3 sm:px-4">
          {SETTINGS_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-2xl px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] transition-all ${activeTab === tab.id
                ? 'bdat-accent-button shadow-[0_14px_30px_rgba(49,95,244,0.18)]'
                : 'bdat-ghost-button text-[color:var(--bdat-muted)] hover:text-[color:var(--bdat-text)]'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 space-y-8 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6 custom-scrollbar">
          {activeTab === 'api' && (
            <div className="space-y-6">

              {/* --- MODE SWITCHER --- */}
              <div className="bg-[#1c2333] border border-white/10 rounded-2xl p-5 mb-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-xs font-bold text-white uppercase tracking-widest mb-1">Applicatie Modus</h4>
                    <p className="text-[10px] text-slate-400">Schakel tussen veilig testen en live gebruik.</p>
                  </div>
                  <div className="flex bg-black/40 p-1 rounded-lg">
                    <button
                      onClick={() => setLocalSettings({ ...localSettings, appMode: 'test' })}
                      className={`px-4 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all ${localSettings.appMode === 'test' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                      Test Modus
                    </button>
                    <button
                      onClick={() => {
                        if (confirm("WAARSCHUWING: In LIVE mode worden echte e-mails verstuurd naar klanten. Weet je het zeker?")) {
                          setLocalSettings({ ...localSettings, appMode: 'live' });
                        }
                      }}
                      className={`px-4 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all ${localSettings.appMode === 'live' ? 'bg-red-500/20 text-red-400 border border-red-500/50' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                      Live Modus
                    </button>
                  </div>
                </div>

                {localSettings.appMode === 'test' && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                    <label className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest">Test E-mailadres (Ontvangt alle mails)</label>
                    <input
                      type="email"
                      value={localSettings.testEmailAddress}
                      onChange={(e) => setLocalSettings({ ...localSettings, testEmailAddress: e.target.value })}
                      placeholder="jouw-email@voorbeeld.nl"
                      className="w-full bg-black/20 border border-emerald-500/30 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    <p className="text-[9px] text-slate-500 italic">In Test Mode worden ALLE e-mails naar dit adres gestuurd ipv naar de klant.</p>
                  </div>
                )}
              </div>

              <div className="space-y-4 rounded-2xl border border-white/10 bg-[#1c2333] p-5">
                <div>
                  <h4 className="text-xs font-bold text-white uppercase tracking-widest">Zoekgebied</h4>
                  <p className="mt-2 text-[10px] leading-5 text-slate-400">
                    Bepaalt waar Lead Zoeker bedrijven en contactgegevens zoekt. Telefoon of e-mail blijft verplicht voordat een lead wordt getoond.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {SEARCH_REGION_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setLocalSettings({ ...localSettings, searchRegion: option.id })}
                      className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                        localSettings.searchRegion === option.id
                          ? 'border-cyan-400/40 bg-cyan-500/12 text-cyan-50 shadow-[0_12px_26px_rgba(6,182,212,0.12)]'
                          : 'border-white/8 bg-white/5 text-slate-300 hover:border-white/15'
                      }`}
                    >
                      <span className="block text-[11px] font-bold uppercase tracking-[0.18em]">{option.label}</span>
                      <span className="mt-2 block text-[10px] leading-4 text-slate-400">{option.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4 rounded-2xl border border-white/10 bg-[#1c2333] p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="text-xs font-bold text-white uppercase tracking-widest">Google Places</h4>
                    <p className="mt-2 text-[10px] leading-5 text-slate-400">
                      Server-side key voor Maps/Places contactverrijking. De key wordt niet in browser storage of projectdata opgeslagen.
                    </p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-[9px] font-bold uppercase tracking-[0.16em] ${
                    serverSettings?.googlePlaces.configured
                      ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200'
                      : 'border-slate-500/30 bg-slate-500/10 text-slate-300'
                  }`}>
                    {serverSettings?.googlePlaces.configured
                      ? `${serverSettings.googlePlaces.source === 'env' ? 'Env' : 'Server'} actief`
                      : 'Niet ingesteld'}
                  </span>
                </div>

                {serverSettings?.googlePlaces.maskedKey && (
                  <div className="rounded-xl border border-white/8 bg-black/20 px-4 py-3 text-[10px] text-slate-300">
                    Actieve key: <span className="font-mono text-slate-100">{serverSettings.googlePlaces.maskedKey}</span>
                  </div>
                )}

                <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
                  <input
                    title="Google Places API Key"
                    type="password"
                    value={googlePlacesKey}
                    onChange={(e) => setGooglePlacesKey(e.target.value)}
                    placeholder={serverSettings?.googlePlaces.configured ? 'Nieuwe key plakken om te vervangen' : 'AIza...'}
                    className="w-full bg-black/20 border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleSaveGooglePlacesKey}
                      disabled={googlePlacesSaving || !googlePlacesKey.trim()}
                      className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-100 transition hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {googlePlacesSaving ? 'Opslaan...' : 'Opslaan'}
                    </button>
                    <button
                      type="button"
                      onClick={handleTestGooglePlacesKey}
                      disabled={googlePlacesTesting || (!googlePlacesKey.trim() && !serverSettings?.googlePlaces.configured)}
                      className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-100 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {googlePlacesTesting ? 'Test...' : 'Test'}
                    </button>
                    <button
                      type="button"
                      onClick={handleClearGooglePlacesKey}
                      disabled={googlePlacesSaving || serverSettings?.googlePlaces.source !== 'server'}
                      className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-rose-100 transition hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Wissen
                    </button>
                  </div>
                </div>

                {googlePlacesStatus && (
                  <p className="text-[10px] leading-5 text-emerald-300">{googlePlacesStatus}</p>
                )}
                {googlePlacesError && (
                  <p className="text-[10px] leading-5 text-rose-300">{googlePlacesError}</p>
                )}
              </div>

              <div className="space-y-4 pt-4 border-t border-white/5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Model Provider</h4>
                    <p className="mt-2 text-[11px] text-slate-400">
                      OpenRouter is de aanbevolen standaard. Daarmee kan de gebruiker makkelijk meegroeien met nieuwe modellen zonder dat wij de app telkens hoeven om te bouwen.
                    </p>
                  </div>
                  {localSettings.provider === AIProvider.OPENROUTER && (
                    <button
                      type="button"
                      onClick={() => applyCostMode(CostMode.FREE_FIRST)}
                      className="rounded-xl border border-indigo-400/30 bg-indigo-500/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-200 transition hover:bg-indigo-500/20"
                    >
                      Gebruik Free Router
                    </button>
                  )}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {PROVIDER_OPTIONS.map((providerOption) => (
                    <button
                      key={providerOption.id}
                      type="button"
                      onClick={() => handleProviderChange(providerOption.id)}
                      className={`rounded-2xl border px-4 py-4 text-left transition-all ${
                        localSettings.provider === providerOption.id
                          ? 'border-indigo-400/40 bg-indigo-500/12 shadow-[0_12px_26px_rgba(79,70,229,0.16)]'
                          : 'border-white/8 bg-white/5 hover:border-white/15'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className={`text-[11px] font-bold uppercase tracking-[0.2em] ${localSettings.provider === providerOption.id ? 'text-indigo-200' : 'text-slate-200'}`}>
                          {providerOption.label}
                        </span>
                        {providerOption.id === AIProvider.OPENROUTER && (
                          <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-200">
                            Aanbevolen
                          </span>
                        )}
                      </div>
                      <p className={`mt-2 text-[11px] leading-5 ${localSettings.provider === providerOption.id ? 'text-slate-300' : 'text-slate-500'}`}>
                        {providerOption.hint}
                      </p>
                    </button>
                  ))}
                </div>

                {localSettings.provider === AIProvider.GEMINI && (
                  <div className="space-y-3 animate-in fade-in slide-in-from-top-1">
                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Gemini API Key</label>
                    <input
                      title="Gemini API Key"
                      type="password"
                      value={localSettings.geminiKey}
                      onChange={(e) => setLocalSettings({ ...localSettings, geminiKey: e.target.value })}
                      placeholder="AIzaSy..."
                      className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                )}

                {localSettings.provider === AIProvider.OPENROUTER && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-top-1">
                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">OpenRouter API Key</label>
                    <input
                      title="OpenRouter API Key"
                      type="password"
                      value={localSettings.openRouterKey}
                      onChange={(e) => setLocalSettings({ ...localSettings, openRouterKey: e.target.value })}
                      placeholder="sk-or-v1-..."
                      className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-[11px] leading-5 text-emerald-100">
                      Tip: laat standaard <span className="font-semibold">{OpenRouterService.AUTO_MODEL_NAME}</span> aan staan. De app probeert gratis modellen eerst en stuurt requests met prijs-sortering.
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      {COST_MODE_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => applyCostMode(option.id)}
                          className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                            localSettings.costMode === option.id
                              ? 'border-emerald-400/40 bg-emerald-500/12 text-emerald-50'
                              : 'border-white/8 bg-white/5 text-slate-300 hover:border-white/15'
                          }`}
                        >
                          <span className="block text-[10px] font-bold uppercase tracking-[0.18em]">{option.label}</span>
                          <span className="mt-2 block text-[10px] leading-4 text-slate-400">{option.hint}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {(localSettings.provider === AIProvider.OLLAMA || localSettings.provider === AIProvider.LMSTUDIO) && (
                  <div className="space-y-3 animate-in fade-in slide-in-from-top-1">
                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Local Endpoint URL</label>
                    <input
                      title="Local Endpoint URL"
                      type="text"
                      value={localSettings.localUrl}
                      onChange={(e) => setLocalSettings({ ...localSettings, localUrl: e.target.value })}
                      placeholder={localSettings.provider === AIProvider.OLLAMA ? "http://localhost:11434" : "http://localhost:1234"}
                      className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    {localSettings.provider === AIProvider.LMSTUDIO && (
                      <>
                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">LM Studio API Key</label>
                        <input
                          title="LM Studio API Key"
                          type="password"
                          value={localSettings.localApiKey || ''}
                          onChange={(e) => setLocalSettings({ ...localSettings, localApiKey: e.target.value })}
                          placeholder="lm-studio-... of leeg als auth uit staat"
                          className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        <p className="text-[9px] text-slate-500 italic">Als LM Studio op /v1/models een 401 geeft, vul hier de server key in.</p>
                      </>
                    )}
                    {localModelsError && (
                      <p className="text-[9px] text-amber-400 italic">{localModelsError}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-4 pt-4 border-t border-white/5">
                <h4 className="text-xs font-bold text-indigo-400 uppercase">Model Selectie</h4>

                {/* --- CHAT MODEL --- */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[9px] font-bold text-slate-500 uppercase">Chat Model</label>
                    <ModelStatusIndicator type="chat" modelId={localSettings.defaultModel} />
                  </div>
                  {localSettings.provider === AIProvider.GEMINI ? (
                    <select
                      title="Select Chat Model"
                      value={localSettings.defaultModel}
                      onChange={(e) => setLocalSettings({ ...localSettings, defaultModel: e.target.value })}
                      className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      {geminiModels.map(m => {
                        let label: string = m;
                        if (m === GeminiModel.FLASH_3) label = "Gemini 2.5 Flash (Stable)";
                        if (m === GeminiModel.PRO_3) label = "Gemini 2.5 Pro (Stable)";
                        if (m === GeminiModel.THINKING_3) label = "Gemini 2.5 Flash Preview (Thinking)";
                        if (m === GeminiModel.LITE_3) label = "Gemini 2.5 Flash-Lite";
                        return <option key={`chat-${m}`} value={m}>{label}</option>;
                      })}
                    </select>
                  ) : localSettings.provider === AIProvider.OPENROUTER ? (
                    <div className="space-y-3">
                      <select
                        title="Select OpenRouter Chat Model"
                        value={localSettings.defaultModel}
                        onChange={(e) => setLocalSettings({ ...localSettings, defaultModel: e.target.value })}
                        className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                        <optgroup label="AANBEVOLEN">
                          <option value={OpenRouterService.AUTO_MODEL_ID}>
                            {OpenRouterService.AUTO_MODEL_NAME} • gratis eerst
                          </option>
                          {localSettings.costMode !== CostMode.FREE_FIRST && (
                            <option value={OpenRouterService.PAID_AUTO_MODEL_ID}>
                              {OpenRouterService.PAID_AUTO_MODEL_NAME} • betaalde fallback
                            </option>
                          )}
                          {orModels.free.slice(0, 5).map((m) => (
                            <option key={`recommended-free-${m.id}`} value={m.id}>
                              {formatOpenRouterModelLabel(m)}
                            </option>
                          ))}
                        </optgroup>
                        {!!orModels.free.length && (
                          <optgroup label="GRATIS / BUDGET">
                            {orModels.free.map((m) => (
                              <option key={`free-${m.id}`} value={m.id}>
                                {formatOpenRouterModelLabel(m)}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {localSettings.costMode !== CostMode.FREE_FIRST && !!orModels.paid.length && (
                          <optgroup label="BETAALD / PREMIUM">
                            {orModels.paid.map((m) => (
                              <option key={`paid-${m.id}`} value={m.id}>
                                {formatOpenRouterModelLabel(m)}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                      <p className="text-[10px] text-slate-500">
                        Gebruik <span className="text-slate-300">{OpenRouterService.AUTO_MODEL_NAME}</span> als goedkope standaard. Betaalde modellen verschijnen pas buiten Free First.
                      </p>
                    </div>
                  ) : (
                    <select
                        title="Select Local Chat Model"
                        value={localSettings.defaultModel}
                        onChange={(e) => setLocalSettings({ ...localSettings, defaultModel: e.target.value })}
                        className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                        {localModels.map(m => <option key={`local-${m}`} value={m}>{m}</option>)}
                        <option value="">Selecteer lokaal model...</option>
                    </select>
                  )}
                </div>

                {/* --- RESEARCH MODEL --- */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[9px] font-bold text-slate-500 uppercase">Onderzoek Model</label>
                    <ModelStatusIndicator type="research" modelId={localSettings.researchModel} />
                  </div>
                  {localSettings.provider === AIProvider.GEMINI ? (
                    <select
                        title="Select Research Model"
                        value={localSettings.researchModel}
                        onChange={(e) => setLocalSettings({ ...localSettings, researchModel: e.target.value })}
                        className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                        {geminiModels.map(m => {
                          let label: string = m;
                          if (m === GeminiModel.FLASH_3) label = "Gemini 2.5 Flash (Stable)";
                          if (m === GeminiModel.PRO_3) label = "Gemini 2.5 Pro (Stable)";
                          if (m === GeminiModel.THINKING_3) label = "Gemini 2.5 Flash Preview (Thinking)";
                          if (m === GeminiModel.LITE_3) label = "Gemini 2.5 Flash-Lite";
                          return <option key={`res-${m}`} value={m}>{label}</option>;
                        })}
                    </select>
                  ) : localSettings.provider === AIProvider.OPENROUTER ? (
                    <div className="space-y-3">
                      <select
                          title="Select OpenRouter Research Model"
                          value={localSettings.researchModel}
                          onChange={(e) => setLocalSettings({ ...localSettings, researchModel: e.target.value })}
                          className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                          <optgroup label="AANBEVOLEN">
                              <option value={OpenRouterService.AUTO_MODEL_ID}>
                                {OpenRouterService.AUTO_MODEL_NAME} • gratis eerst
                              </option>
                              {localSettings.costMode !== CostMode.FREE_FIRST && (
                                <option value={OpenRouterService.PAID_AUTO_MODEL_ID}>
                                  {OpenRouterService.PAID_AUTO_MODEL_NAME} • premium router
                                </option>
                              )}
                              {orModels.free.slice(0, 5).map((m) => (
                                <option key={`recommended-research-free-${m.id}`} value={m.id}>
                                  {formatOpenRouterModelLabel(m)}
                                </option>
                              ))}
                          </optgroup>
                          {!!orModels.free.length && (
                            <optgroup label="GRATIS / BUDGET">
                              {orModels.free.map((m) => (
                                <option key={`res-free-${m.id}`} value={m.id}>
                                  {formatOpenRouterModelLabel(m)}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {localSettings.costMode !== CostMode.FREE_FIRST && !!orModels.paid.length && (
                            <optgroup label="BETAALD / PREMIUM">
                              {orModels.paid.map((m) => (
                                <option key={`res-paid-${m.id}`} value={m.id}>
                                  {formatOpenRouterModelLabel(m)}
                                </option>
                              ))}
                            </optgroup>
                          )}
                      </select>
                      <p className="text-[10px] text-slate-500">
                        Deep dive gebruikt in Free First een meerstaps workflow met gratis modellen. Best Quality zet pas bewust premium research aan.
                      </p>
                    </div>
                  ) : (
                    <select
                        title="Select Local Research Model"
                        value={localSettings.researchModel}
                        onChange={(e) => setLocalSettings({ ...localSettings, researchModel: e.target.value })}
                        className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-4 py-3 text-white text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                        {localModels.map(m => <option key={`res-local-${m}`} value={m}>{m}</option>)}
                        <option value="">Selecteer lokaal model...</option>
                    </select>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[9px] font-bold text-slate-500 uppercase">Denkbudget</label>
                    <span className="text-[9px] text-indigo-400 font-mono">{localSettings.thinkingBudget} tokens</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="32768"
                    step="1024"
                    value={localSettings.thinkingBudget}
                    onChange={(e) => setLocalSettings({ ...localSettings, thinkingBudget: parseInt(e.target.value) })}
                    className="w-full accent-indigo-500"
                  />
                  <p className="text-[8px] text-slate-600">Hogere budgetten zorgen voor diepere redenering bij ondersteunde Gemini 2.5-modellen.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <div className="bdat-settings-card rounded-[1.75rem] p-5 sm:p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="max-w-2xl">
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] bdat-settings-muted">Kleurregeling</p>
                    <h3 className="mt-2 text-lg font-bold text-[color:var(--bdat-text)]">Eigen UI-kleuren voor vlakken en velden</h3>
                    <p className="mt-2 text-sm leading-6 bdat-settings-muted">
                      Pas hier de app-shell, panelen en invoervelden aan. De preview werkt live zolang dit venster open staat.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => applyThemePreset({ id: 'default', name: 'Default', colors: DEFAULT_UI_THEME })}
                      className="bdat-ghost-button rounded-xl px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em]"
                    >
                      Reset Basis
                    </button>
                    <button
                      type="button"
                      onClick={saveCurrentThemeAsPreset}
                      className="bdat-accent-button rounded-xl px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em]"
                    >
                      Preset Opslaan
                    </button>
                  </div>
                </div>

                <div className="mt-6 grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                  <div className="rounded-[1.5rem] border border-[color:var(--bdat-border)] p-4" style={{ background: localSettings.uiTheme.canvas }}>
                    <div className="rounded-[1.25rem] border p-4 shadow-sm" style={{ background: localSettings.uiTheme.sidebar, borderColor: localSettings.uiTheme.border }}>
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: localSettings.uiTheme.mutedText }}>Preview</p>
                          <h4 className="mt-1 text-base font-bold" style={{ color: localSettings.uiTheme.text }}>BDAT Light Ops</h4>
                        </div>
                        <button
                          type="button"
                          className="rounded-xl px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em]"
                          style={{ background: localSettings.uiTheme.accent, color: localSettings.uiTheme.accentText }}
                        >
                          Actie
                        </button>
                      </div>

                      <div className="grid gap-3 md:grid-cols-[0.75fr_1.25fr]">
                        <div className="rounded-[1.25rem] border p-3" style={{ background: localSettings.uiTheme.surfaceAlt, borderColor: localSettings.uiTheme.border }}>
                          <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: localSettings.uiTheme.mutedText }}>Sidebar</p>
                          <div className="mt-3 space-y-2">
                            <div className="rounded-xl px-3 py-2 text-xs font-semibold" style={{ background: localSettings.uiTheme.surface, color: localSettings.uiTheme.text }}>Werkruimte</div>
                            <div className="rounded-xl px-3 py-2 text-xs" style={{ background: localSettings.uiTheme.surfaceAlt, color: localSettings.uiTheme.mutedText }}>Assistent</div>
                            <div className="rounded-xl px-3 py-2 text-xs" style={{ background: localSettings.uiTheme.surfaceAlt, color: localSettings.uiTheme.mutedText }}>Config</div>
                          </div>
                        </div>

                        <div className="rounded-[1.25rem] border p-4" style={{ background: localSettings.uiTheme.surface, borderColor: localSettings.uiTheme.border }}>
                          <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: localSettings.uiTheme.mutedText }}>Panel + Velden</p>
                          <div className="mt-3 grid gap-3">
                            <div className="rounded-xl border px-4 py-3 text-sm" style={{ background: localSettings.uiTheme.input, borderColor: localSettings.uiTheme.border, color: localSettings.uiTheme.text }}>
                              Voorbeeld invoerveld
                            </div>
                            <div className="rounded-xl border p-4" style={{ background: localSettings.uiTheme.surfaceAlt, borderColor: localSettings.uiTheme.border }}>
                              <p className="text-sm font-semibold" style={{ color: localSettings.uiTheme.text }}>Samenhangende kaart</p>
                              <p className="mt-1 text-xs leading-5" style={{ color: localSettings.uiTheme.mutedText }}>
                                Dit laat direct zien hoe vlakken, tekst en accent met elkaar werken.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[1.5rem] border border-[color:var(--bdat-border)] bg-[color:var(--bdat-surface-alt)] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] bdat-settings-muted">Presets</p>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      {localSettings.uiThemePresets.map((preset) => (
                        <div key={preset.id} className={`rounded-2xl border p-3 transition-all ${isPresetActive(preset) ? 'shadow-[0_16px_28px_rgba(49,95,244,0.14)]' : ''}`} style={{ borderColor: isPresetActive(preset) ? localSettings.uiTheme.accent : localSettings.uiTheme.border, background: preset.colors.surface }}>
                          <button
                            type="button"
                            onClick={() => applyThemePreset(preset)}
                            className="w-full text-left"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <p className="text-sm font-bold" style={{ color: preset.colors.text }}>{preset.name}</p>
                                <p className="mt-1 text-[11px]" style={{ color: preset.colors.mutedText }}>
                                  {defaultPresetIds.has(preset.id) ? 'Standaard preset' : 'Opgeslagen preset'}
                                </p>
                              </div>
                              <div className="flex gap-1">
                                {[preset.colors.canvas, preset.colors.surface, preset.colors.accent].map((color) => (
                                  <span key={color} className="h-5 w-5 rounded-full border border-white/60" style={{ background: color }} />
                                ))}
                              </div>
                            </div>
                          </button>
                          {!defaultPresetIds.has(preset.id) && (
                            <button
                              type="button"
                              onClick={() => deleteThemePreset(preset.id)}
                              className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-rose-500 transition hover:text-rose-600"
                            >
                              Verwijder preset
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                {THEME_FIELDS.map((field) => (
                  <div key={field.key} className="bdat-settings-card rounded-[1.5rem] p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-[0.22em] bdat-settings-muted">{field.label}</label>
                        <p className="mt-1 text-xs leading-5 bdat-settings-muted">{field.hint}</p>
                      </div>
                      <input
                        type="color"
                        value={localSettings.uiTheme[field.key]}
                        onChange={(e) => updateThemeColor(field.key, e.target.value)}
                        className="h-12 w-12 cursor-pointer rounded-2xl border border-[color:var(--bdat-border)] p-1"
                        title={field.label}
                      />
                    </div>
                    <input
                      type="text"
                      value={localSettings.uiTheme[field.key]}
                      onChange={(e) => updateThemeColor(field.key, e.target.value)}
                      className="mt-4 w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'persona' && (
            <div className="space-y-8">
              <div className="space-y-3">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">Systeem Instructies</label>
                <textarea
                  value={localSettings.systemPrompt}
                  onChange={(e) => setLocalSettings({ ...localSettings, systemPrompt: e.target.value })}
                  className="w-full bg-[#1c2333] border border-white/10 rounded-2xl p-5 text-sm h-32 text-white outline-none focus:ring-2 focus:ring-indigo-500 resize-none transition-all"
                />
              </div>
            </div>
          )}

          {activeTab === 'regels' && (
            <div className="space-y-3">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">Algemene Regels</label>
              <textarea
                value={localSettings.globalRules}
                onChange={(e) => setLocalSettings({ ...localSettings, globalRules: e.target.value })}
                className="w-full bg-[#1c2333] border border-white/10 rounded-2xl p-5 text-sm h-64 text-white outline-none focus:ring-2 focus:ring-indigo-500 resize-none transition-all"
              />
            </div>
          )}

          {activeTab === 'stem' && (
            <div className="space-y-6">
              {/* Call Agent Deactiveren Toggle */}
              <div className="flex items-center justify-between bg-rose-950/25 border border-rose-500/25 rounded-2xl px-5 py-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-white uppercase tracking-[0.1em]">Call Agent Deactiveren</label>
                  <p className="text-[10px] text-slate-400">Schakel alle telefonie en belfuncties volledig uit</p>
                </div>
                <button
                  type="button"
                  onClick={() => setLocalSettings({ ...localSettings, disableCallAgent: !localSettings.disableCallAgent })}
                  className={`w-12 h-6 rounded-full transition-all relative ${localSettings.disableCallAgent ? 'bg-rose-600' : 'bg-slate-700'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${localSettings.disableCallAgent ? 'left-7' : 'left-1'}`} />
                </button>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] block mb-1">Live Spraak Engine</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {voiceProvidersList.map((provider) => {
                    const isActive = (localSettings.liveVoiceProvider || LiveVoiceProvider.GEMINI) === provider.id;
                    return (
                      <button
                        key={provider.id}
                        type="button"
                        onClick={() => setLocalSettings({ ...localSettings, liveVoiceProvider: provider.id })}
                        className={`group relative flex flex-col items-start p-5 rounded-2xl border text-left transition-all duration-300 cursor-pointer ${
                          isActive 
                            ? provider.bgActive 
                            : 'bg-[#1c2333]/50 border-white/5 hover:border-white/10 hover:bg-[#1c2333] hover:scale-[1.01]'
                        }`}
                      >
                        <div className="flex w-full items-start justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div className={`p-2.5 rounded-xl ${
                              isActive ? 'bg-white/10 text-white' : 'bg-white/5 text-slate-400 group-hover:text-white transition-colors'
                            }`}>
                              {provider.icon}
                            </div>
                            <div>
                              <h3 className="text-sm font-bold text-white tracking-wide">{provider.title}</h3>
                              <span className="text-[9px] text-slate-500 font-mono uppercase tracking-wider">{provider.tech}</span>
                            </div>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full border text-[8px] font-bold tracking-wider uppercase ${provider.badgeClass}`}>
                            {provider.badge}
                          </span>
                        </div>
                        
                        <p className="mt-3.5 text-xs text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                          {provider.desc}
                        </p>

                        {isActive && (
                          <div className="absolute bottom-3 right-3 w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-4 rounded-2xl border border-emerald-400/15 bg-emerald-500/5 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-100">Voice Lab</h4>
                    <p className="mt-2 text-[10px] leading-5 text-slate-400">
                      Test elke stap los: mic, STT-status, OpenRouter brain, ElevenLabs stem en de volledige loop.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`rounded-full border px-3 py-1 text-[9px] font-bold uppercase tracking-[0.16em] ${
                      voiceServerSettings?.elevenLabs?.configured ? 'border-emerald-400/30 text-emerald-200' : 'border-amber-400/30 text-amber-200'
                    }`}>
                      ElevenLabs {voiceServerSettings?.elevenLabs?.configured ? 'actief' : 'mist key'}
                    </span>
                    <span className={`rounded-full border px-3 py-1 text-[9px] font-bold uppercase tracking-[0.16em] ${
                      voiceServerSettings?.deepgram?.configured ? 'border-emerald-400/30 text-emerald-200' : 'border-amber-400/30 text-amber-200'
                    }`}>
                      Deepgram {voiceServerSettings?.deepgram?.configured ? 'actief' : 'degraded'}
                    </span>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">ElevenLabs API Key</span>
                    <input
                      type="password"
                      value={elevenLabsKey}
                      onChange={(e) => setElevenLabsKey(e.target.value)}
                      placeholder={voiceServerSettings?.elevenLabs?.maskedKey || 'xi-...'}
                      className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">ElevenLabs Voice ID</span>
                    <input
                      type="text"
                      value={elevenLabsVoiceId}
                      onChange={(e) => setElevenLabsVoiceId(e.target.value)}
                      placeholder="21m00Tcm4TlvDq8ikWAM"
                      className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Deepgram API Key</span>
                    <input
                      type="password"
                      value={deepgramKey}
                      onChange={(e) => setDeepgramKey(e.target.value)}
                      placeholder={voiceServerSettings?.deepgram?.maskedKey || 'Deepgram key voor productie-STT'}
                      className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">ElevenLabs Agent ID (WebRTC)</span>
                    <input
                      type="text"
                      value={localSettings.elevenLabsAgentId || ''}
                      onChange={(e) => setLocalSettings({ ...localSettings, elevenLabsAgentId: e.target.value })}
                      placeholder="Voer je ElevenLabs Conversational Agent ID in"
                      className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </label>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={saveElevenLabsSettings}
                    disabled={voiceLabBusy === 'elevenlabs-save' || (!elevenLabsKey.trim() && !elevenLabsVoiceId.trim())}
                    className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    ElevenLabs opslaan
                  </button>
                  <button
                    type="button"
                    onClick={saveDeepgramSettings}
                    disabled={voiceLabBusy === 'deepgram-save' || !deepgramKey.trim()}
                    className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Deepgram opslaan
                  </button>
                  <button
                    type="button"
                    onClick={clearDeepgramSettings}
                    disabled={voiceLabBusy === 'deepgram-clear' || voiceServerSettings?.deepgram?.source !== 'server'}
                    className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Deepgram wissen
                  </button>
                </div>

                <div className="grid gap-2 sm:grid-cols-4">
                  {([
                    ['mic', 'Test Mic'],
                    ['brain', 'Test Brain'],
                    ['voice', 'Test Voice'],
                    ['full', 'Full Loop'],
                  ] as const).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => void runVoiceLab(id)}
                      disabled={Boolean(voiceLabBusy)}
                      className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-200 transition hover:border-emerald-400/35 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {voiceLabBusy === id ? 'Bezig...' : label}
                    </button>
                  ))}
                </div>

                {voiceLabStatus && (
                  <p className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-[10px] leading-5 text-emerald-200">{voiceLabStatus}</p>
                )}
                {voiceLabError && (
                  <p className="rounded-xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-[10px] leading-5 text-rose-200">{voiceLabError}</p>
                )}
              </div>


              <div className="flex items-center justify-between bg-[#1c2333] border border-white/5 rounded-xl px-5 py-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-white uppercase tracking-[0.1em]">Automatisch voorlezen</label>
                  <p className="text-[8px] text-slate-500 italic">Lees chatberichten direct voor na generatie.</p>
                </div>
                <button
                  onClick={() => setLocalSettings({ ...localSettings, autoSpeak: !localSettings.autoSpeak })}
                  className={`w-12 h-6 rounded-full transition-all relative ${localSettings.autoSpeak ? 'bg-indigo-600' : 'bg-slate-700'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${localSettings.autoSpeak ? 'left-7' : 'left-1'}`} />
                </button>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">Selecteer Stem</label>
                <select
                  value={localSettings.voiceName}
                  onChange={(e) => setLocalSettings({ ...localSettings, voiceName: e.target.value })}
                  className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-5 py-4 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {VOICES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">Spraak Provider</label>
                <select
                  value={localSettings.ttsProvider}
                  onChange={(e) => setLocalSettings({ ...localSettings, ttsProvider: e.target.value as TTSProvider })}
                  className="w-full bg-[#1c2333] border border-white/5 rounded-xl px-5 py-4 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value={TTSProvider.GEMINI}>Gemini Live Native</option>
                  <option value={TTSProvider.ELEVENLABS}>ElevenLabs Natural Dutch</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="space-y-8">
              <div className="rounded-2xl border border-white/10 bg-[#1c2333] p-5 space-y-5">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">Ingelogde gebruiker</p>
                  <h4 className="mt-2 text-lg font-bold text-white">{currentUser?.name || 'Account'}</h4>
                  <p className="mt-1 text-xs text-slate-400">{currentUser?.email || 'Geen gebruiker gevonden'}</p>
                </div>

                <div className="grid gap-4">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Naam</span>
                    <input
                      type="text"
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </label>

                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">E-mail</span>
                    <input
                      type="email"
                      value={accountEmail}
                      onChange={(e) => setAccountEmail(e.target.value)}
                      className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </label>
                </div>

                <button
                  onClick={handleSaveProfile}
                  disabled={profileSaving || !accountName.trim() || !accountEmail.trim()}
                  className="px-6 py-3 rounded-xl bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-[0.22em] transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-400"
                >
                  {profileSaving ? 'Opslaan...' : 'Profiel Opslaan'}
                </button>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#1c2333] p-5 space-y-5">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">Wachtwoord</p>
                  <h4 className="mt-2 text-lg font-bold text-white">Wachtwoord wijzigen</h4>
                  <p className="mt-1 text-xs text-slate-400">Gebruik minimaal 8 tekens en sla daarna opnieuw in met de nieuwe gegevens in als je elders nog open tabs hebt.</p>
                </div>

                <div className="grid gap-4">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Huidig wachtwoord</span>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </label>

                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Nieuw wachtwoord</span>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </label>

                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Herhaal nieuw wachtwoord</span>
                    <input
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </label>
                </div>

                <button
                  onClick={handleChangePassword}
                  disabled={passwordSaving || !currentPassword || !newPassword || !confirmNewPassword}
                  className="px-6 py-3 rounded-xl bg-[#5249f3] text-white text-[10px] font-bold uppercase tracking-[0.22em] transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#6359f5]"
                >
                  {passwordSaving ? 'Wijzigen...' : 'Wachtwoord Wijzigen'}
                </button>

                {(accountStatus || accountError) && (
                  <div className={`rounded-xl px-4 py-3 text-sm ${accountError ? 'border border-rose-400/30 bg-rose-500/10 text-rose-200' : 'border border-emerald-400/30 bg-emerald-500/10 text-emerald-200'}`}>
                    {accountError || accountStatus}
                  </div>
                )}
              </div>

              {currentUser?.role === 'ADMIN' && (
                <div className="rounded-2xl border border-white/10 bg-[#1c2333] p-5 space-y-5">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">Gebruikersbeheer</p>
                    <h4 className="mt-2 text-lg font-bold text-white">Extra admins en resets</h4>
                    <p className="mt-1 text-xs text-slate-400">Handig voor VPS-beheer: maak extra admin-accounts aan en reset wachtwoorden zonder direct in de database te hoeven werken.</p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Naam</span>
                      <input
                        type="text"
                        value={newUserName}
                        onChange={(e) => setNewUserName(e.target.value)}
                        className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </label>

                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">E-mail</span>
                      <input
                        type="email"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </label>

                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Tijdelijk wachtwoord</span>
                      <input
                        type="password"
                        value={newUserPassword}
                        onChange={(e) => setNewUserPassword(e.target.value)}
                        className="mt-2 w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </label>
                  </div>

                  <button
                    onClick={handleCreateUser}
                    disabled={creatingUser || !newUserName.trim() || !newUserEmail.trim() || !newUserPassword.trim()}
                    className="px-6 py-3 rounded-xl bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-[0.22em] transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-400"
                  >
                    {creatingUser ? 'Aanmaken...' : 'Extra Admin Maken'}
                  </button>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h5 className="text-sm font-bold text-white">Bestaande accounts</h5>
                      {usersLoading && <span className="text-xs text-slate-400">Laden...</span>}
                    </div>

                    <div className="space-y-3">
                      {managedUsers.map((user) => (
                        <div key={user.id} className="rounded-xl border border-white/10 bg-[#0f172a] p-4 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-bold text-white">{user.name}</p>
                              <p className="text-xs text-slate-400">{user.email}</p>
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-300">{user.role}</span>
                          </div>

                          <div className="grid gap-3 md:grid-cols-[1fr_auto_auto]">
                            <input
                              type="password"
                              value={resetPasswords[user.id] || ''}
                              onChange={(e) => setResetPasswords((current) => ({ ...current, [user.id]: e.target.value }))}
                              placeholder="Nieuw wachtwoord"
                              className="w-full bg-[#111827] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                            <button
                              onClick={() => handleResetManagedUserPassword(user.id)}
                              disabled={resettingUserId === user.id || !(resetPasswords[user.id] || '').trim()}
                              className="px-4 py-3 rounded-xl border border-indigo-400/30 text-indigo-200 text-[10px] font-bold uppercase tracking-[0.18em] transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-500/10"
                            >
                              {resettingUserId === user.id ? 'Reset...' : 'Reset Wachtwoord'}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={deletingUserId === user.id || user.id === currentUser.id}
                              className="px-4 py-3 rounded-xl border border-rose-400/30 text-rose-200 text-[10px] font-bold uppercase tracking-[0.18em] transition disabled:opacity-40 disabled:cursor-not-allowed hover:bg-rose-500/10"
                            >
                              {deletingUserId === user.id ? 'Verwijderen...' : 'Verwijder'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {(usersStatus || usersError) && (
                    <div className={`rounded-xl px-4 py-3 text-sm ${usersError ? 'border border-rose-400/30 bg-rose-500/10 text-rose-200' : 'border border-emerald-400/30 bg-emerald-500/10 text-emerald-200'}`}>
                      {usersError || usersStatus}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-[color:var(--bdat-border)] px-4 py-4 sm:px-6">
          <button onClick={handleClose} className="bdat-ghost-button rounded-xl px-6 py-3 text-[10px] font-bold uppercase tracking-[0.18em]">Annuleren</button>
          <button onClick={handleSaveSettings} className="bdat-accent-button rounded-xl px-8 py-3 text-[10px] font-bold uppercase tracking-[0.18em] shadow-[0_16px_30px_rgba(49,95,244,0.18)]">Opslaan</button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
